import { HttpRequestError } from "@workspace/base-api/fetchJson";

import type { Order, OrderComp } from "../types";

/** HA-658 (AC3) — what the reason prompt says when a comp is confirmed with no reason. */
export const COMP_REASON_REQUIRED_MESSAGE = "A reason is required to comp";

/** HA-658 (D-HA-61 61-1) — the mark on a comped line, and the badge on a fully comped order. */
export const COMPD_LABEL = "Comp'd (£0)";

const ORDER_HAS_PAYMENT_CODE = "ORDER_HAS_PAYMENT";

/** Every line any of the order's comps covers. */
export function compedItemIds(
	order: Pick<Order, "comps"> | null | undefined,
): Set<string> {
	return new Set((order?.comps ?? []).flatMap((c) => c.itemIds));
}

/** HA-658 (AC1) — each comped line's reason, shown beside its Comp'd (£0) mark. A line a comp took reads that comp's reason. */
export function compReasonsByItem(
	order: Pick<Order, "comps"> | null | undefined,
): Map<string, string> {
	const reasons = new Map<string, string>();
	for (const comp of order?.comps ?? []) {
		for (const id of comp.itemIds) reasons.set(id, comp.reason);
	}
	return reasons;
}

/** The whole-order comp, if there is one (a line's comp is never it). */
export function wholeOrderComp(
	order: Pick<Order, "comps"> | null | undefined,
): OrderComp | undefined {
	return order?.comps?.find((c) => c.scope === "Order");
}

/**
 * HA-658 R1 (35-15) — comps brought the order to £0.00 with no payment, so it is Completed but not sold: Remove Comp can reopen it.
 * The server's flag when it sent one (the comp routes' answers do); otherwise read from the order itself, as the venue's own copy of
 * the order doesn't carry it: Completed, fully comped, and nothing paid.
 */
export function isCompletedByComps(
	order: Pick<
		Order,
		"status" | "payments" | "fullyComped" | "completedByComps"
	>,
): boolean {
	if (order.status !== "Completed") return false;
	if (order.completedByComps !== undefined) return order.completedByComps;
	return (
		order.fullyComped === true &&
		!order.payments.some(
			(p) => p.status === "Completed" || p.status === "Pending",
		)
	);
}

/**
 * How a refused comp or Remove Comp is told (a toast for each 409). A payment already taken has a fixed line; every other refusal
 * shows the server's own words, which name the rule more exactly than the till could; an error with no answer is a connection one.
 */
export function compRefusalMessage(error: unknown): string {
	if (error instanceof HttpRequestError) {
		if (error.problem?.code === ORDER_HAS_PAYMENT_CODE) {
			return "This Order has taken a payment, so its comps can't change.";
		}
		const detail =
			(typeof error.problem?.detail === "string" && error.problem.detail) ||
			(typeof error.problem?.title === "string" && error.problem.title);
		if (detail) return detail.trim();
	}
	return "The comp couldn't be sent. Check the connection and try again.";
}
